export interface DatadogRumConfig {
  readonly version: 1
  readonly applicationId: string
  readonly clientToken: string
  readonly site: 'datadoghq.com'
  readonly service: string
  readonly env: string
  readonly proxyUrl: string
  readonly workerUrl?: string
  readonly sessionSampleRate: number
  readonly profilingSampleRate: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isValidWorkerUrl(workerUrl: unknown, documentOrigin?: string): workerUrl is string | undefined {
  if (workerUrl === undefined) return true
  if (typeof workerUrl !== 'string' || !workerUrl.trim()) return false

  try {
    const url = new URL(workerUrl, documentOrigin ?? 'https://example.invalid')
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:'
    return isHttp && (!documentOrigin || url.origin === documentOrigin)
  } catch {
    return false
  }
}

export function isValidDatadogRumConfig(config: unknown, documentOrigin?: string): config is DatadogRumConfig {
  return Boolean(
    isRecord(config) &&
    config.version === 1 &&
    typeof config.applicationId === 'string' &&
    config.applicationId &&
    typeof config.clientToken === 'string' &&
    config.clientToken &&
    config.site === 'datadoghq.com' &&
    typeof config.service === 'string' &&
    config.service &&
    typeof config.env === 'string' &&
    config.env &&
    typeof config.proxyUrl === 'string' &&
    config.proxyUrl &&
    isValidWorkerUrl(config.workerUrl, documentOrigin) &&
    typeof config.sessionSampleRate === 'number' &&
    config.sessionSampleRate >= 0 &&
    config.sessionSampleRate <= 100 &&
    typeof config.profilingSampleRate === 'number' &&
    config.profilingSampleRate >= 0 &&
    config.profilingSampleRate <= 100,
  )
}

export function getDatadogRumConfig(root: Document = document): DatadogRumConfig | undefined {
  const configElement = root.getElementById('datadog-rum-config')
  if (!configElement) return undefined

  try {
    const config: unknown = JSON.parse(configElement.textContent || '')
    return isValidDatadogRumConfig(config, root.location.origin) ? config : undefined
  } catch {
    return undefined
  }
}
